import { BaseLayout } from '@hub/layout/BaseLayout/BaseLayout';
import type { NodeContact } from '@hub/models/api/node-contact.types';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { useBearingHubPageStore } from '@hub/hooks/states/useBearingHubPageStore';
import { getLocale, IBH_PRODUCTS_PER_PAGE, IbhFilterKeys } from '@hub/libs/constants';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BearingHubHelper, type SortingState } from '@hub/libs/bearing-hub.helper';
import { Trans, useTranslation } from 'next-i18next';
import { useNextHashQueryParams } from '@hub/hooks/useNextQueryParams';
import { DimensionForm } from '@hub/bearing-hub/BearingHubPage/DimensionForm';
import {
  type IbhFilterValues,
  type IbhFilterValuesStore,
  useActiveFiltersAmount,
  useIbhFilterValuesStore,
  useIbhHasFilters,
} from '@hub/hooks/states/useIbhFilterValuesStore';
import { useBearingPartsApi } from '@hub/bearing-hub/BearingHubPage/parts/useBearingPartsApi';
import { BearingsTable } from '@hub/bearing-hub/BearingHubPage/BearingsTable/BearingsTable';
import { useBearingSimilarPartsApi } from '@hub/bearing-hub/BearingHubPage/parts/useBearingSimilarPartsApi';
import type { LanguageAlternate } from '@hub/models/data';
import { Collapsible } from '@hub/common/Collapsible/Collapsible';
import { MethodAndMaterialSection } from '@hub/bearing-hub/BearingHubPage/BearingsFilter/MethodAndMaterialSection';
import { TemperatureRangeSection } from '@hub/bearing-hub/BearingHubPage/BearingsFilter/TemperatureRangeSection';
import { NutritionSectorSection } from '@hub/bearing-hub/BearingHubPage/BearingsFilter/NutritionSectorSection';
import { EnvironmentSection } from '@hub/bearing-hub/BearingHubPage/BearingsFilter/EnvironmentSection';
import { ElectricalPropertiesSection } from '@hub/bearing-hub/BearingHubPage/BearingsFilter/ElectricalPropertiesSection';
import { SurfacePressureSection } from '@hub/bearing-hub/BearingHubPage/BearingsFilter/SurfacePressureSection';
import { FilterResetLink } from '@hub/common/FilterResetLink/FilterResetLink';
import { LifeTimePopUpInfo } from '@hub/bearing-hub/libs/BearingsLifetime/LifeTimePopUpInfo';
import { CompareMaterialsModal } from '@hub/bearing-hub/BearingHubPage/BearingsFilter/CompareMaterialsModal/CompareMaterialsModal';
import type { Headers } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import { useBearingsTableStore } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import { SeoMeta } from '@hub/common/SeoMeta/SeoMeta';
import type { IbhLifeTimeConfig, LifetimeParameters } from '@hub/bearing-hub/common/types';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';
import { DEFAULT_QUANTITY } from '@hub/bearing-hub/common/constants';
import { useLifetimeParametersStore } from '@hub/bearing-hub/libs/BearingsLifetime/useIbhLifetimeParametersValuesStore';
import { queryParamsHandler } from '@hub/bearing-hub/BearingHubPage/libs/queryParamsHandler';
import { productDetailsSlug } from '@hub/bearing-hub/common/link.factory';
import { Logger } from '@icommerce/logger';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { IbhEventCategory, TrackedIbhEventActions } from '@hub/bearing-hub/common/tracking.config';
import { ShapeNavigation } from './shapes/ShapeNavigation/ShapeNavigation';
import { useSessionStore } from '@hub/hooks/states/useSessionStore';
import { useDimensionsStore } from '@hub/hooks/states/useIbhDimensionsValuesStore';
import { TablePaginator } from '@hub/bearing-hub/BearingHubPage/BearingsTable/TablePaginator';
import { scrollToElement } from '@hub/libs/utils';
import { useCopyLinkToClipboard } from '@hub/hooks/useCopyToClipboard';
import type {
  IbhPartsLifetimeResponseDto,
  IbhPartsResponseDto,
  IbhProductionMethodResponseDto,
} from '@igus/iglidur-bearing-hub-api';
import type {
  IbhMaterialResponseDto,
  IbhProcedure,
  IbhProductionMethods,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { useUnitSystem } from '@hub/bearing-hub/common/useUnitSystem/useUnitSystem';
import { QuantityInput } from '@hub/bearing-hub/BearingHubPage/QuantityInput';
import dynamic from 'next/dynamic';
import { CollapsibleTheme } from '@hub/common/Collapsible/BaseCollapsible';
import { LifetimeParametersForm } from '@hub/bearing-hub/libs/BearingsLifetime/LifetimeParametersForm/LifetimeParametersForm';
import { ProgressBar } from '@hub/common/ProgressBar/ProgressBar';
import { ProgressIndicator, ProgressShape, ProgressThickness } from '@icommerce/ui';
import { useFeatureFlag } from '@hub/libs/hub-config';
import { useMaterials } from '@hub/bearing-hub/common/materials/useMaterials';
import { useAddToShoppingCart } from '@hub/bearing-hub/common/basket/useAddToShoppingCart';
import { ComplianceModal } from '@hub/bearing-hub/common/basket/ComplianceModal';
import { IglideStandardModal } from '@hub/common/IglideStandardModal/IglideStandardModal';
import { Link } from '@hub/common/Link/Link';
import { useHasDynamicPrice } from '@hub/hooks/useHasDynamicPrice';
import { useBearingPartsLifetimeApi } from '@hub/bearing-hub/BearingHubPage/parts/useBearingPartsLifetimeApi';
import type { DimensionKey, IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { ShapeDimensionsDefinitionForm } from '@hub/bearing-hub/common/dimensions/dimensions';
import { UnitSystem } from '@igus/kopla-domain';
import type { LayoutData } from '@hub/page-helpers/getInitialLayoutData';
import { Switch } from '@hub/bearing-hub/common/Switch/Switch';

const EMPTY_BEARING_PARTS: IbhPartsResponseDto[] = [];

export interface ExtendedBearingHubPageProps extends BearingHubPageProps {
  contact?: NodeContact | null;
}

export interface BearingHubPageProps {
  languageAlternates: LanguageAlternate[];
  selectedShape: ShapeDescriptor;
  availableShapes: ShapeDescriptor[];
  layout: LayoutData;
  materials: IbhMaterialResponseDto[];
  productionMethods: IbhProductionMethodResponseDto[];
  bearingParts: IbhPartsResponseDto[];
  initialBearingPartsLifetime?: IbhPartsLifetimeResponseDto;
  initialUnitSystem: UnitSystem;
}

export const BearingHubPage = ({
  languageAlternates,
  selectedShape,
  availableShapes,
  layout,
  materials,
  productionMethods,
  bearingParts,
  initialBearingPartsLifetime,
  initialUnitSystem,
}: BearingHubPageProps) => {
  const contact = useBearingHubPageStore(state => state.contact);
  const query = useNextHashQueryParams();
  const { t, i18n } = useTranslation(['category/productFilter', 'common']);
  const {
    lifetimeParameters,
    formUnitSystem,
    setFormUnitSystem,
    getLifetimeConfigPayload,
    getDefaultLifetimeParameters,
    resetLifetimeParametersToDefault,
  } = useLifetimeParametersStore();
  const router = useRouter();
  const locale = getLocale(router);
  const session = useSessionStore(state => state.session);
  const { CustomEvent } = usePiwikPro();
  const { unitSystem, setUnitSystem } = useUnitSystem(initialUnitSystem);
  const hasDynamicPrices = useHasDynamicPrice();
  const [currentPage, setCurrentPage] = useState(1);
  const [hideCustomParts, setHideCustomParts] = useState(false);
  const [desktopDimensionsValid, setDesktopDimensionsValid] = useState(true);
  const [mobileDimensionsValid, setMobileDimensionsValid] = useState(true);
  const dimensionsValid = desktopDimensionsValid && mobileDimensionsValid;
  const [showFilterReset, setShowFilterReset] = useState(false);
  const [clientReady, setClientReady] = useState(false);
  const [lifetimeParamsInitialized, setLifetimeParamsInitialized] = useState(false);
  const [activeLifeTimeModal, setActiveLifeTimeModal] = useState(false);
  const [activeCompareMaterialsModal, setActiveCompareMaterialsModal] = useState(false);
  const [activeIglideStandardModal, setActiveIglideStandardModal] = useState(false);
  const [showSpinner, toggleSpinner] = useState(false);
  const [quantityValue, setQuantityValue] = useState(DEFAULT_QUANTITY);
  const [isSticky, setIsSticky] = useState(false);
  const [isExceeded, setIsExceeded] = useState(false);
  const [currentStickyFilterWidth, setCurrentStickyFilterWidth] = useState<number>();
  const [currentPageLocation, setCurrentPageLocation] = useState('');

  const { copyLinkToClipboard } = useCopyLinkToClipboard(
    t(`SHARE_LINK_TOAST_SUCCESS`, { ns: 'bearing-hub/bearingHub' })
  );
  const filterReleaseLocales = useFeatureFlag('ibh_useFilterReleaseLocales');
  const Survicate = dynamic(() => import('@hub/bearing-hub/common/survicate/survicate').then(mod => mod.SurvicateApp), {
    ssr: false,
  });
  const filterValues = useIbhFilterValuesStore(state => state.filterValues) as IbhFilterValuesStore['filterValues'];
  const selectedProductionMethods = filterValues.productionMethod as IbhProductionMethods[] | undefined;

  const { filteredMaterials, materialFilterOptions, selectedMaterials } = useMaterials({
    shapeKey: selectedShape.shapeKey,
    baseMaterials: materials,
    filterValues,
    session,
    productionMethods: selectedProductionMethods,
    enabled: true,
  });

  const {
    addToShoppingCart,
    articlesAddingToBasket,
    pendingComplianceArticle,
    setPendingComplianceArticle,
    addPendingComplianceArticleToShoppingCart,
  } = useAddToShoppingCart({
    session,
    quantity: quantityValue,
    shape: selectedShape.shapeKey,
    setupCostIsLoading: false,
    i18n,
    complianceConfirmationEnabled: true,
  });

  const setDimension = useDimensionsStore(state => state.setDimension);
  const filterControl = useIbhFilterValuesStore(state => state.filterControl) as IbhFilterValuesStore['filterControl'];
  const resetSideBarFilters = useIbhFilterValuesStore(
    state => state.resetSideBarFilters
  ) as IbhFilterValuesStore['reset'];
  const setFilterValue = useIbhFilterValuesStore(
    state => state.setFilterValue
  ) as IbhFilterValuesStore['setFilterValue'];
  const { currentSortingHeaderState, currentSortingHeaderKey, tableHeaders, isTableFullScreen, setIsTableFullScreen } =
    useBearingsTableStore();
  const hasFilterValues = useIbhHasFilters();
  const activeFiltersAmount = useActiveFiltersAmount();
  const mainIbhInfoPagePath = BearingHubHelper.getMainIbhInfoPagePath(locale);

  const updateQueryParams = (
    obj: Partial<{
      selectedShape: ShapeDescriptor;
      unitSystem: UnitSystem;
      currentSortingHeaderState: SortingState;
      currentSortingHeaderKey: Headers;
      currentPage: number;
      quantityInputValue: number;
      filterValues: IbhFilterValues;
      lifetimeParameters: LifetimeParameters;
      formUnitSystem: UnitSystem;
    }> = {}
  ) => {
    const formUnitSystemFromHash = query.formUnitSystem
      ? queryParamsHandler.parseFormUnitSystem(query.formUnitSystem, formUnitSystem)
      : undefined;
    const updatedFormUnitSystem =
      obj.formUnitSystem ??
      formUnitSystemFromHash ??
      (formUnitSystem !== (obj.unitSystem ?? unitSystem) ? formUnitSystem : undefined);
    const newQueryParams = queryParamsHandler.getUpdatedQueryParams(
      obj.selectedShape ?? selectedShape,
      obj.unitSystem ?? unitSystem,
      obj.currentSortingHeaderState ?? currentSortingHeaderState,
      obj.currentSortingHeaderKey ?? currentSortingHeaderKey,
      obj.currentPage ?? currentPage,
      obj.quantityInputValue ?? quantityValue,
      obj.filterValues ?? filterValues,
      obj.lifetimeParameters ?? lifetimeParameters,
      locale,
      undefined,
      updatedFormUnitSystem,
      hideCustomParts
    );

    queryParamsHandler.updateRouterQuery(router, newQueryParams);
  };

  const manageSpinner = () => {
    let timer: NodeJS.Timeout;
    const handleStart = (url: string) => {
      const productDetails = url.includes('product-details');
      const differentShape = !url.includes(router.query?.shape as string);
      if (!timer) {
        timer = setTimeout(() => {
          (productDetails || differentShape) && toggleSpinner(true);
        }, 300);
      }
    };
    const handleComplete = () => {
      if (timer) {
        clearTimeout(timer);
      }
      toggleSpinner(false);
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleComplete);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleComplete);
      if (timer) {
        clearTimeout(timer);
      }
    };
  };

  const reinitEvergageTracking = (url: string) => {
    if (!url.includes(productDetailsSlug)) {
      return;
    }
    try {
      if (typeof Evergage !== 'undefined' && typeof Evergage.reinit === 'function') {
        Evergage.reinit();
      }
    } catch (e) {
      Logger.error({ msg: (e as Error).message });
    }
  };

  const loadInitQuantity = () => {
    const initQuantity = Number(query.quantity);

    if (initQuantity > 0) {
      setQuantityValue(initQuantity);
    }
  };

  const filteredProductionMethods =
    filterValues[IbhFilterKeys.PRODUCTION_METHOD] &&
    (filterValues[IbhFilterKeys.PRODUCTION_METHOD] as IbhProductionMethods[]).length > 0
      ? (filterValues[IbhFilterKeys.PRODUCTION_METHOD] as IbhProductionMethods[])
      : undefined;

  const filteredProcedures =
    filterValues[IbhFilterKeys.PROCEDURES] && (filterValues[IbhFilterKeys.PROCEDURES] as IbhProcedure[]).length > 0
      ? (filterValues[IbhFilterKeys.PROCEDURES] as IbhProcedure[])
      : undefined;

  let dimensions: IbhDimensions;
  if (filterValues.dimensions) {
    // add valid dimensions for currently selected shape
    const validDimensionKeys = Object.values(ShapeDimensionsDefinitionForm[selectedShape.shapeKey]);
    const validDimensions = Object.keys(filterValues.dimensions as IbhDimensions).reduce(
      (acc: IbhDimensions, key: DimensionKey) => {
        if (validDimensionKeys.includes(key) && filterValues.dimensions[key] !== null) {
          acc[key] = filterValues.dimensions[key] as number;
        }
        return acc;
      },
      {} as IbhDimensions
    );
    dimensions = {
      ...validDimensions,
    };
  }

  let newFilteredProductionMethods: string[];
  if (filteredProductionMethods) {
    newFilteredProductionMethods = [];
    newFilteredProductionMethods = newFilteredProductionMethods.concat(filteredProductionMethods);
  }
  const {
    data: bearingPartsData,
    allDoneCount,
    status,
    isLoading: bearingPartsLoading,
  } = useBearingPartsApi({
    enabled: (clientReady && Object.values(query).length > 0) || (clientReady && hasDynamicPrices), // Make the initial API request when the URL contains filter configuration or when the server currency differs from the client currency
    shape: selectedShape.shapeKey,
    materials: selectedMaterials.length ? selectedMaterials : filteredMaterials.map(material => material.id),
    productionMethods: newFilteredProductionMethods as IbhProductionMethods[],
    procedures: filteredProcedures,
    unitSystem,
    quantity: quantityValue,
    dimensions,
    session,
    filterReleaseLocales,
    serverData: bearingParts,
  });

  const receivedBearingParts = dimensionsValid ? bearingPartsData : EMPTY_BEARING_PARTS;
  const isLoading = dimensionsValid && bearingPartsLoading;
  const catalogParts = useMemo(
    () => receivedBearingParts.filter(part => !part.articleNumber.startsWith('TEMP-')),
    [receivedBearingParts]
  );
  const customPartsCount = receivedBearingParts.length - catalogParts.length;
  const visibleBearingParts = hideCustomParts ? catalogParts : receivedBearingParts;

  const { lifeTimeData, maxLifeTimeReference, isLifetimeLoading, refetchLifetime } = useBearingPartsLifetimeApi({
    enabled: clientReady && !isLoading && receivedBearingParts.length > 0,
    shape: selectedShape.shapeKey,
    materials: selectedMaterials.length ? selectedMaterials : filteredMaterials.map(material => material.id),
    unitSystem,
    quantity: quantityValue,
    productionMethods: newFilteredProductionMethods as IbhProductionMethods[],
    dimensions,
    lifetimeConfig: getLifetimeConfigPayload() as IbhLifeTimeConfig,
    session,
    initialData: initialBearingPartsLifetime,
  });

  const bearingSimilarPartsResponse = useBearingSimilarPartsApi({
    enabled: dimensionsValid && clientReady && !isLoading && receivedBearingParts.length === 0,
    shape: selectedShape.shapeKey,
    materials: selectedMaterials.length ? selectedMaterials : materials.map(material => material.id),
    productionMethods: newFilteredProductionMethods as IbhProductionMethods[],
    unitSystem,
    quantity: quantityValue,
    dimensions,
    session,
  });

  const totalPages = useMemo(
    () => Math.ceil(visibleBearingParts.length / IBH_PRODUCTS_PER_PAGE),
    [visibleBearingParts]
  );

  const receivedBearingSimilarParts = dimensionsValid ? bearingSimilarPartsResponse.data : EMPTY_BEARING_PARTS;

  const initializeLifetimeParams = () => {
    setFormUnitSystem(queryParamsHandler.parseFormUnitSystem(query.formUnitSystem, initialUnitSystem));
    resetLifetimeParametersToDefault(queryParamsHandler.getDataFromParams(query.lifetimeParameters as string));
  };

  const initializeCurrentPage = () => {
    const pageNum = parseInt(query.page as string, 10);
    if (Number.isNaN(pageNum) || pageNum > totalPages) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    } else if (!Number.isNaN(pageNum) && currentPage !== pageNum) {
      setCurrentPage(pageNum);
    }
  };

  const tempratureRangeValues = [
    typeof (filterValues[IbhFilterKeys.TEMPERATURE_RANGE_MIN] as number | undefined) !== 'undefined'
      ? (filterValues[IbhFilterKeys.TEMPERATURE_RANGE_MIN] as number)
      : null,
    typeof (filterValues[IbhFilterKeys.TEMPERATURE_RANGE_MAX] as number | undefined) !== 'undefined'
      ? (filterValues[IbhFilterKeys.TEMPERATURE_RANGE_MAX] as number)
      : null,
  ];
  const widenShortKey = selectedShape.widenShortKeySchema;

  const onScroll = useCallback(() => {
    const stickyFilterElem = window.document.getElementById('sticky-filter');
    if (typeof stickyFilterElem?.getBoundingClientRect().width !== 'undefined') {
      setCurrentStickyFilterWidth(stickyFilterElem.getBoundingClientRect().width);

      const headerElem = window.document.getElementById('product-table');
      if (typeof headerElem?.getBoundingClientRect().top !== 'undefined') {
        const limit = headerElem.getBoundingClientRect().bottom + document.documentElement.scrollTop + 46;
        const exceeds =
          document.documentElement.scrollTop + stickyFilterElem.children[0].getBoundingClientRect().height > limit;
        if (
          window.pageYOffset > headerElem.getBoundingClientRect().top + document.documentElement.scrollTop &&
          visibleBearingParts.length > 10
        ) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
        setIsExceeded(exceeds);
      }
    }
  }, [visibleBearingParts]);

  const onLifetimeParametersApply = () => {
    CustomEvent.trackEvent(
      IbhEventCategory,
      TrackedIbhEventActions.calculateServiceLife,
      JSON.stringify(getLifetimeConfigPayload())
    );
    updateQueryParams({ lifetimeParameters, formUnitSystem });
    void refetchLifetime();
  };

  const onLifetimeParametersReset = () => {
    const defaultLifetime = getDefaultLifetimeParameters();
    resetLifetimeParametersToDefault();
    updateQueryParams({ lifetimeParameters: defaultLifetime, formUnitSystem });
  };

  useEffect(() => manageSpinner(), [router]);

  useEffect(() => {
    router.events.on('routeChangeComplete', reinitEvergageTracking);
    return () => {
      router.events.off('routeChangeComplete', reinitEvergageTracking);
    };
  }, [router]);

  useEffect(() => {
    window.document.body.classList[showSpinner ? 'add' : 'remove']('overflow-hidden');
    return () => {
      window.document.body.classList.remove('overflow-hidden');
    };
  }, [showSpinner]);

  useEffect(() => {
    if (clientReady && lifetimeParamsInitialized) {
      updateQueryParams();
    }
  }, [
    filterValues,
    totalPages,
    currentPage,
    selectedShape,
    quantityValue,
    tableHeaders,
    currentSortingHeaderState,
    currentSortingHeaderKey,
    router,
    unitSystem,
    clientReady,
    lifetimeParamsInitialized,
    hideCustomParts,
  ]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  /**
   * This useEffect disables prefetching on this whole page to reduce load generated by
   * users hovering over links
   */
  useEffect(() => {
    const { prefetch } = router;
    router.prefetch = async () => {};
    return () => {
      router.prefetch = prefetch;
    };
  }, [router]);

  useEffect(() => {
    if (clientReady) {
      loadInitQuantity();
      initializeCurrentPage();
      initializeLifetimeParams();
      setHideCustomParts(query.hideCustomParts === 'true');
      setLifetimeParamsInitialized(true);
    }
  }, [clientReady]);

  useEffect(() => {
    setClientReady(!!session && router.isReady);
    setCurrentPageLocation(window.location.origin);
  }, [session, router]);

  useEffect(() => {
    setShowFilterReset(hasFilterValues || quantityValue !== DEFAULT_QUANTITY);
  }, [hasFilterValues, quantityValue]);

  useEffect(() => {
    setCurrentPage(1);
  }, [currentSortingHeaderState, visibleBearingParts.length]);

  const lifetimeInfo = (
    <div className="lg:mx-6 mt-6">
      <h4>{t(`LIFETIME.SERVICE_LIFE_TITLE`, { ns: 'bearing-hub/bearingHub' })}</h4>
      <span>
        {t(`LIFETIME.SERVICE_LIFE_DESCRIPTION`, { ns: 'bearing-hub/bearingHub' })}
        <span> </span>
        <Link
          flex={false}
          text={t('LIFETIME.SERVICE_LIFE_LINK', { ns: 'bearing-hub/bearingHub' })}
          href={t('LIFETIME.SERVICE_LIFE_URL', { ns: 'bearing-hub/bearingHub' })}
          primary
          external
        />
      </span>
    </div>
  );

  const headSection = (
    <>
      <h1 className="my-12 leading-none lg:text-[5.6rem] text-[3.2rem] font-semibold text-black">
        {t(`SHAPES.${selectedShape.shapeKey}.HEADLINE`, { ns: 'bearing-hub/bearingHub' })}
      </h1>
      <div className="mb-8">
        <Trans className="mb-8" i18nKey={`SHAPES.${selectedShape.shapeKey}.DESCRIPTION`} ns="bearing-hub/bearingHub" />
      </div>
    </>
  );

  const collapsibleSection = (
    <div className="hidden lg:block mb-6" data-testid="collapsible-section">
      <Collapsible
        disableScrolling
        activeTab={1}
        styles={{ padding: '5px 15px' }}
        theme={CollapsibleTheme.ORANGE}
        keepContentInDom
        items={[
          {
            title: t(`HEADLINES.SELECT_SHAPE`, { ns: 'bearing-hub/bearingHub' }),
            content: (
              <ShapeNavigation
                useTiles
                selectedShape={selectedShape}
                availableShapes={availableShapes}
                query={query}
                locale={locale}
              />
            ),
          },
          {
            title: t(`HEADLINES.DEFINE_DIMENSIONS`, { ns: 'bearing-hub/bearingHub' }),
            content: (
              <DimensionForm
                shapeKey={selectedShape.shapeKey}
                onValidationChange={setDesktopDimensionsValid}
                onReset={() => setHideCustomParts(false)}
                widenShortKey={widenShortKey}
                setFilterValue={setFilterValue}
                updateMetricType={value => {
                  const unitSystemValue = value ? UnitSystem.imperial : UnitSystem.metric;
                  CustomEvent.trackEvent(
                    IbhEventCategory,
                    TrackedIbhEventActions.switchUnitSystemSelect,
                    unitSystemValue.toString()
                  );
                  setUnitSystem(unitSystemValue);
                }}
                unitSystem={unitSystem}
              />
            ),
          },
          {
            title: t(`LIFETIME.CALCULATE_SERVICE_LIFE`, { ns: 'bearing-hub/bearingHub' }),
            content: (
              <>
                {lifetimeInfo}
                <LifetimeParametersForm
                  isEmbedded
                  unitSystem={unitSystem}
                  onCalculate={onLifetimeParametersApply}
                  onReset={onLifetimeParametersReset}
                  onFormUnitSystemChange={(nextFormUnitSystem, convertedLifetimeParameters) =>
                    updateQueryParams({
                      formUnitSystem: nextFormUnitSystem,
                      lifetimeParameters: convertedLifetimeParameters,
                    })
                  }
                  shapeKey={selectedShape.shapeKey}
                />
              </>
            ),
          },
        ]}
      />
    </div>
  );
  const spinner = showSpinner ? (
    <div
      role="dialog"
      className="fixed flex flex-col justify-center items-center top-0 left-0 bottom-0 right-0 z-50 h-full bg-gray-600 bg-opacity-60"
    >
      <div className="w-28 h-28 rounded-full animate-spin border-solid border-[#F07D00] border-t-transparent border-2 " />
      <span className="mt-6 text-igus-primary">{t(`COMMON.SELECTION_LOADING`, { ns: 'bearing-hub/bearingHub' })}</span>
    </div>
  ) : null;

  const toggleFilterButton = (
    <div>
      <button
        className="text-igus-primary"
        type="button"
        data-testid="toggle-filter-button"
        onClick={setIsTableFullScreen}
      >
        <Icon size={10} color={IconColor.ORANGE} id={isTableFullScreen ? IconID.FILTER_ALT : IconID.FILTER_ALT_OFF} />
        <span>{t(`${isTableFullScreen ? 'SHOW_FILTER' : 'HIDE_FILTER'}`, { ns: 'bearing-hub/bearingHub' })}</span>
      </button>
      <span className="ml-2">
        ({clientReady ? activeFiltersAmount : 0} {t(`COMMON.ACTIVE`, { ns: 'bearing-hub/bearingHub' })})
      </span>
    </div>
  );

  const resetFilterButton = showFilterReset && (
    <FilterResetLink
      text={t('PRODUCT_OVERVIEW.FILTER.REMOVE_ALL_FILTERS')}
      dataTestId="remove_all_filters"
      reset={() => {
        CustomEvent.trackEvent(IbhEventCategory, TrackedIbhEventActions.removeAllFiltersSelect);
        resetSideBarFilters();
        setQuantityValue(DEFAULT_QUANTITY);
        scrollToElement('dimensions-form');
      }}
    />
  );

  const copyToClipboardButton = (
    <button
      className="inline-flex items-center gap-4 text-igus-primary"
      type="button"
      onClick={async () => {
        CustomEvent.trackEvent(IbhEventCategory, TrackedIbhEventActions.shareLink);
        const url = currentPageLocation + router.asPath;
        await copyLinkToClipboard(url);
      }}
    >
      <Icon size={6} color={IconColor.ORANGE} id={IconID.SHARE} />
      <span>{t(`SHARE_CONFIG_TEXT_LINK`, { ns: 'bearing-hub/bearingHub' })}</span>
    </button>
  );

  const resultInfoSection = (
    <div className="grid gap-x-8 gap-y-4 mb-4 lg:grid-cols-[minmax(0,1fr)_auto]">
      <div className="flex flex-col items-start gap-x-8 gap-y-2 md:flex-row md:flex-wrap md:items-center">
        <h3 className="text-3xl font-semibold text-black" data-testid="suitable-offers-count">
          {t(`HEADLINES.CHOOSE_FROM_PRODUCTS`, {
            count: visibleBearingParts.length,
            ns: 'bearing-hub/bearingHub',
          })}
        </h3>
        <p data-testid="bearing-parts-counts">
          {t('PART_COUNTS', {
            catalogCount: catalogParts.length,
            customCount: customPartsCount,
            ns: 'bearing-hub/bearingHub',
          })}
        </p>
        <div data-testid="hide-custom-parts">
          <Switch
            checked={hideCustomParts}
            onChange={value => {
              setHideCustomParts(value);
              setCurrentPage(1);
            }}
            disabled={!clientReady || isLoading || customPartsCount === 0}
            optionTwo={t('HIDE_CUSTOM_PARTS', { ns: 'bearing-hub/bearingHub' })}
            toggleOnly
            theme="secondary"
            customStyles="!m-0 flex items-center [&>label]:ml-0 [&>label]:shrink-0"
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-2 lg:col-start-1">
        <div className="hidden lg:flex lg:flex-wrap lg:items-center gap-x-8 gap-y-2">
          {toggleFilterButton}
          {resetFilterButton}
        </div>
        {copyToClipboardButton}
      </div>
      <div className="hidden lg:block lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-end">
        <QuantityInput
          initialQuantity={quantityValue}
          onQuantityChange={value => {
            setQuantityValue(value || 1);
          }}
        />
      </div>
    </div>
  );

  const mobileFilterSection = (
    <div className="block lg:hidden xl:hidden mb-16 w-full relative">
      <Collapsible
        styles={{ padding: '5px 15px' }}
        items={[
          {
            title: t(`HEADLINES.SELECT_SHAPE`, { ns: 'bearing-hub/bearingHub' }),
            content: (
              <div className="mt-8 pl-4">
                <ShapeNavigation
                  selectedShape={selectedShape}
                  availableShapes={availableShapes}
                  query={query}
                  locale={locale}
                  useTiles={false}
                />
              </div>
            ),
          },
          {
            title: t(`HEADLINES.DEFINE_DIMENSIONS`, { ns: 'bearing-hub/bearingHub' }),
            content: (
              <DimensionForm
                shapeKey={selectedShape.shapeKey}
                onValidationChange={setMobileDimensionsValid}
                onReset={() => setHideCustomParts(false)}
                widenShortKey={widenShortKey}
                setFilterValue={setFilterValue}
                updateMetricType={value => {
                  const unitSystemValue = value ? UnitSystem.imperial : UnitSystem.metric;
                  CustomEvent.trackEvent(
                    IbhEventCategory,
                    TrackedIbhEventActions.switchUnitSystemSelect,
                    unitSystemValue.toString()
                  );
                  setUnitSystem(unitSystemValue);
                }}
                unitSystem={unitSystem}
              />
            ),
          },
          {
            title: t(`HEADLINES.METHOD_AND_MATERIAL_FILTER`, { ns: 'bearing-hub/bearingHub' }),
            content: (
              <div className="mt-8">
                <MethodAndMaterialSection
                  materialFilterOptions={materialFilterOptions}
                  productionMethods={productionMethods}
                  filterValues={filterValues}
                  setFilterValue={setFilterValue}
                  filterControl={filterControl}
                  availableParts={visibleBearingParts.length}
                  setActiveCompareMaterialsModal={setActiveCompareMaterialsModal}
                  activeCompareMaterialsModal={activeCompareMaterialsModal}
                  activeIglideStandardModal={activeIglideStandardModal}
                  setActiveIglideStandardModal={setActiveIglideStandardModal}
                  isCollapsible
                />
              </div>
            ),
          },
          {
            title: t(`FILTERS.SECTIONS.TEMPERATURE_RANGE`, { ns: 'bearing-hub/bearingHub' }),
            content: (
              <div className="mt-8">
                <TemperatureRangeSection
                  tempratureRangeValues={tempratureRangeValues}
                  setFilterValue={setFilterValue}
                  isCollapsible
                  unitSystem={unitSystem}
                />
              </div>
            ),
          },
          {
            title: t(`FILTERS.SECTIONS.NUTRITION_SECTOR`, { ns: 'bearing-hub/bearingHub' }),
            content: (
              <div className="mt-8">
                <NutritionSectorSection
                  filterControl={filterControl}
                  filterValues={filterValues}
                  setFilterValue={setFilterValue}
                  isCollapsible
                />
              </div>
            ),
          },
          {
            title: t(`FILTERS.SECTIONS.ENVIRONMENT`, { ns: 'bearing-hub/bearingHub' }),
            content: (
              <div className="mt-8">
                <EnvironmentSection
                  filterControl={filterControl}
                  filterValues={filterValues}
                  setFilterValue={setFilterValue}
                  isCollapsible
                />
              </div>
            ),
          },
          {
            title: t(`FILTERS.SECTIONS.ELECTRICAL_PROPERTIES`, { ns: 'bearing-hub/bearingHub' }),
            content: (
              <div className="mt-8">
                <ElectricalPropertiesSection
                  filterControl={filterControl}
                  filterValues={filterValues}
                  setFilterValue={setFilterValue}
                  isCollapsible
                />
              </div>
            ),
          },
          {
            title: t(`FILTERS.SECTIONS.SURFACE_PRESSURE`, { ns: 'bearing-hub/bearingHub' }),
            content: (
              <div className="mt-8">
                <SurfacePressureSection
                  filterValues={filterValues}
                  setFilterValue={setFilterValue}
                  isCollapsible
                  unitSystem={unitSystem}
                />
              </div>
            ),
          },
          {
            title: t(`LIFETIME.CALCULATE_SERVICE_LIFE`, { ns: 'bearing-hub/bearingHub' }),
            content: (
              <>
                {lifetimeInfo}
                <LifetimeParametersForm
                  isEmbedded
                  onCalculate={onLifetimeParametersApply}
                  onReset={onLifetimeParametersReset}
                  onFormUnitSystemChange={(nextFormUnitSystem, convertedLifetimeParameters) =>
                    updateQueryParams({
                      formUnitSystem: nextFormUnitSystem,
                      lifetimeParameters: convertedLifetimeParameters,
                    })
                  }
                  shapeKey={selectedShape.shapeKey}
                  unitSystem={unitSystem}
                />
              </>
            ),
          },
        ]}
      />
      <div className="border-t-2">
        {showFilterReset && (
          <FilterResetLink
            text={t('PRODUCT_OVERVIEW.FILTER.REMOVE_ALL_FILTERS')}
            dataTestId="remove_all_filters"
            reset={() => {
              CustomEvent.trackEvent(IbhEventCategory, TrackedIbhEventActions.removeAllFiltersSelect);
              resetSideBarFilters();
              setQuantityValue(DEFAULT_QUANTITY);
              scrollToElement('dimensions-form');
            }}
          />
        )}
      </div>
    </div>
  );

  const filterSection = (
    <div className="flex-col hidden lg:flex lg:w-3/12 lg:mr-8 lg:mb-0 mb-8 mt-0 lg:mt-[52px] xl:mt-0">
      <div className={`igus-text-styles ${isSticky ? 'sticky top-0' : 'relative'}`}>
        <div
          id="sticky-filter"
          data-testid="filter_container"
          className={isSticky && !isExceeded ? `sticky top-0 z-20` : 'relative'}
          style={{
            ...(isSticky && currentStickyFilterWidth
              ? {
                  width: `${currentStickyFilterWidth}px`,
                }
              : {}),
          }}
        >
          <div className="w-full p-4 bg-gray-50 relative">
            <h3 className="mb-4 text-2xl font-semibold text-black">
              {t(`HEADLINES.FILTER_PRODUCTS`, { ns: 'bearing-hub/bearingHub' })}
            </h3>
            {clientReady && (
              <MethodAndMaterialSection
                materialFilterOptions={materialFilterOptions}
                productionMethods={productionMethods}
                filterValues={filterValues}
                setFilterValue={setFilterValue}
                filterControl={filterControl}
                availableParts={visibleBearingParts.length}
                setActiveCompareMaterialsModal={setActiveCompareMaterialsModal}
                activeCompareMaterialsModal={activeCompareMaterialsModal}
                activeIglideStandardModal={activeIglideStandardModal}
                setActiveIglideStandardModal={setActiveIglideStandardModal}
              />
            )}
            <div>
              <span id="filter_label_temperature_range" className="block mb-6 font-bold">
                {t(`FILTERS.SECTIONS.TEMPERATURE_RANGE`, { ns: 'bearing-hub/bearingHub' })}
              </span>

              <TemperatureRangeSection
                tempratureRangeValues={tempratureRangeValues}
                setFilterValue={setFilterValue}
                unitSystem={unitSystem}
              />
            </div>
            <div>
              <span id="filter_label_nutrition_sector" className="block mb-6 font-bold">
                {t(`FILTERS.SECTIONS.NUTRITION_SECTOR`, { ns: 'bearing-hub/bearingHub' })}
              </span>

              <NutritionSectorSection
                filterControl={filterControl}
                filterValues={filterValues}
                setFilterValue={setFilterValue}
              />
            </div>
            <div>
              <span id="filter_label_environment" className="block mb-6 font-bold">
                {t(`FILTERS.SECTIONS.ENVIRONMENT`, { ns: 'bearing-hub/bearingHub' })}
              </span>

              <EnvironmentSection
                filterControl={filterControl}
                filterValues={filterValues}
                setFilterValue={setFilterValue}
              />
            </div>
            <div>
              <span id="filter_label_electrical_properties" className="block mb-6 font-bold">
                {t(`FILTERS.SECTIONS.ELECTRICAL_PROPERTIES`, { ns: 'bearing-hub/bearingHub' })}
              </span>

              <ElectricalPropertiesSection
                filterControl={filterControl}
                filterValues={filterValues}
                setFilterValue={setFilterValue}
              />
            </div>
            <div className="relative">
              <span id="filter_label_surface_pressure" className="block mb-6 font-bold">
                {t(`FILTERS.SECTIONS.SURFACE_PRESSURE`, { ns: 'bearing-hub/bearingHub' })}
              </span>

              <SurfacePressureSection
                filterValues={filterValues}
                setFilterValue={setFilterValue}
                unitSystem={unitSystem}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const tableSection = (
    <div className={`w-full ${isTableFullScreen ? '' : 'lg:w-9/12'} z-[1]`}>
      <BearingsTable
        currentPage={currentPage}
        lastKnownValidResponse={visibleBearingParts}
        bearingPartsResponseStatus={status}
        isLoading={isLoading && allDoneCount === 0}
        maxLifeTimeReference={maxLifeTimeReference}
        quantity={quantityValue}
        totalPages={totalPages}
        selectedShape={selectedShape}
        filterValues={filterValues}
        unitSystem={unitSystem}
        bearingPartsLifetime={lifeTimeData}
        isLifetimeLoading={isLifetimeLoading}
        similarParts={receivedBearingSimilarParts} // FIXME: align types with parts-light response
        onChoose={newDimensions => {
          setDimension(selectedShape.shapeKey, {
            ...(filterValues.dimensions as IbhDimensions),
            ...newDimensions,
          });
        }}
        setActiveModal={setActiveLifeTimeModal}
        clientReady={clientReady && !!session}
        onQuantityChange={value => {
          setQuantityValue(value || 1);
        }}
        addToShoppingCart={addToShoppingCart}
        articlesAddingToBasket={articlesAddingToBasket}
      />
      {visibleBearingParts.find(record => record.setupCost.value > 0) && !isLoading && (
        <p data-testid="price-delivery-info" className="font-medium text-xs text-igus-primary my-6">
          * {t(`PRICE_AND_DELIVERY_TIMES_CHECK`, { ns: 'bearing-hub/bearingHub' })}
        </p>
      )}
      {totalPages && !isLoading ? (
        <div className="sm:hidden lg:table flex flex-row mt-8 -ml-6">
          <TablePaginator
            page={currentPage}
            total={totalPages}
            onChangePage={page => {
              scrollToElement('product-table');
              setCurrentPage(page);
            }}
          />
        </div>
      ) : null}
    </div>
  );

  const progressBarSection = (
    <div className="flex justify-end w-full">
      <div className="w-full lg:w-[calc(75%-16px)]">
        {isLoading && (
          <div className="flex items-center gap-2">
            <ProgressIndicator
              label={t('COMMON.LOADING', { ns: 'free-form-hub/freeFormHubCommon' })}
              shape={ProgressShape.Circular}
              thickness={ProgressThickness.Thin}
              size={20}
              className="shrink-0 mb-4"
            />
            <div className="flex-1 min-w-0">
              <ProgressBar
                progressBarText={t('COMMON.SELECTION_LOADING', {
                  current: allDoneCount,
                  ns: 'free-form-hub/freeFormHubCommon',
                })}
                currentProgressCount={allDoneCount}
                maxProgressCount={2}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
  return (
    <>
      <SeoMeta
        categoryName={selectedShape.shapePath}
        languageAlternates={languageAlternates}
        totalPages={totalPages}
        productImage={BearingHubHelper.getWidenImageUrl(widenShortKey, selectedShape.widenShortKeyIcon)}
        webTrendCGNKey="iglidur"
        metaTitle={t(`SHAPES.${selectedShape.shapeKey}.META_TITLE`, { ns: 'bearing-hub/bearingHub' })}
        metaDescription={t(`SHAPES.${selectedShape.shapeKey}.META_DESCRIPTION`, { ns: 'bearing-hub/bearingHub' })}
      />
      <BaseLayout
        breadcrumbItems={[
          {
            isExternal: true,
            title: t(`GOTO_CATEGORY_PAGE`, { ns: 'bearing-hub/bearingHub' }),
            url: mainIbhInfoPagePath,
            key: mainIbhInfoPagePath,
          },
          {
            title: t(`SHAPES.${selectedShape.shapeKey}.HEADLINE`, { ns: 'bearing-hub/bearingHub' }),
            url: null,
            key: selectedShape.shapePath,
            parentKeys: [mainIbhInfoPagePath],
          },
        ]}
        layout={layout}
        contact={contact}
      >
        {headSection}
        {spinner}
        {collapsibleSection}
        {resultInfoSection}
        {mobileFilterSection}
        {progressBarSection}
        <div className="flex flex-row min-h-[580px]">
          {!isTableFullScreen && filterSection}
          {tableSection}
        </div>
        <LifeTimePopUpInfo activeModal={activeLifeTimeModal} setActiveModal={value => setActiveLifeTimeModal(value)} />
        <CompareMaterialsModal
          activeModal={activeCompareMaterialsModal}
          setActiveModal={value => setActiveCompareMaterialsModal(value)}
          materials={materials}
          parts={visibleBearingParts}
          setFilterMaterial={setFilterValue}
        />
        <IglideStandardModal
          header={t(`HEADLINES.IGLIDE_STANDARDS_MODAL`, { ns: 'bearing-hub/bearingHub' })}
          activeModal={activeIglideStandardModal}
          setActiveModal={setActiveIglideStandardModal}
        />
        <ComplianceModal
          visible={!!pendingComplianceArticle}
          onClose={() => setPendingComplianceArticle(null)}
          onConfirm={addPendingComplianceArticleToShoppingCart}
        />
        <Survicate />
      </BaseLayout>
    </>
  );
};
